import { AlertTriangle, CheckCircle2, ListChecks, ShieldAlert, ShieldQuestion } from "lucide-react";
import { Badge, Stars } from "../../components/ui/Badge";
import { reviewFlagLabel, riskFlagLabel, sevenStepSummary, VERIFICATION_LEVEL_LABELS } from "../../lib/research";

const MAX_SIGNALS = 4;
const MAX_NEXT_STEPS = 3;

function verdictHeadline({ level, hardRisks, reviewCount }) {
  if (hardRisks || level === "high_risk") return { tone: "danger", icon: ShieldAlert, text: "发现高风险信号，报价前请人工核实" };
  if (reviewCount) return { tone: "warning", icon: AlertTriangle, text: `有 ${reviewCount} 项待核实，建议先向客户补充确认` };
  if (level === "high") return { tone: "success", icon: CheckCircle2, text: "客户身份证据较充分，可按常规流程跟进" };
  if (level === "medium") return { tone: "info", icon: CheckCircle2, text: "已有部分公开证据，关键信息建议再确认" };
  return { tone: "neutral", icon: ShieldQuestion, text: "公开证据有限，建议先向客户补充信息" };
}

/** One-screen answer to "is this customer credible, and what do I do next?". */
export default function ResearchVerdict({ inquiry, researchDetail, sources, onOpenRating }) {
  const snapshot = inquiry.rating_snapshot || {};
  const level = researchDetail?.verification_level || inquiry.verification_level || "pending";
  const riskFlags = researchDetail?.risk_flags || snapshot.risk_flags || [];
  const signals = [
    ...riskFlags.filter((flag) => !flag.startsWith("REVIEW_")).map((flag) => ({ key: flag, tone: "danger", label: riskFlagLabel(flag) })),
    ...riskFlags.filter((flag) => flag.startsWith("REVIEW_")).map((flag) => ({ key: flag, tone: "warning", label: riskFlagLabel(flag) })),
    ...(snapshot.review_flags || []).map((flag) => ({ key: flag, tone: "warning", label: reviewFlagLabel(flag) })),
  ];
  const hardRisks = signals.filter((item) => item.tone === "danger").length;
  const headline = verdictHeadline({ level, hardRisks, reviewCount: signals.length - hardRisks });
  const nextSteps = [...new Set([
    ...(snapshot.next_verification_questions || []),
    ...(researchDetail?.business_brief?.next_actions || []),
  ])].slice(0, MAX_NEXT_STEPS);
  const verified = sources.filter((source) => source.stageKey === "verified").length;
  const withBody = sources.filter((source) => source.stageKey === "body_fetched").length;
  const sevenStep = sevenStepSummary(inquiry.sop_compatibility || snapshot.sop_compatibility);
  const HeadlineIcon = headline.icon;
  return (
    <section className={`research-verdict is-${headline.tone}`} aria-label="背调结论">
      <header className="research-verdict__headline">
        <HeadlineIcon size={18} />
        <strong>{headline.text}</strong>
        {inquiry.price_protection ? <Badge tone="warning">报价保护开启</Badge> : null}
      </header>
      <div className="research-verdict__facts">
        <span>
          <small>商业价值</small>
          <span className="research-verdict__rating">
            <Stars value={inquiry.business_value_rating} />
            <button type="button" onClick={onOpenRating}>评分依据</button>
          </span>
        </span>
        <span><small>身份可信度</small><strong>{VERIFICATION_LEVEL_LABELS[level] || "待核验"}</strong></span>
        <span>
          <small>证据来源</small>
          <strong>{sources.length ? `已核验 ${verified} · 有正文 ${withBody} · 共 ${sources.length}` : "暂无来源"}</strong>
        </span>
      </div>
      {sevenStep ? (
        <div className="research-verdict__sop" aria-label="七步背调">
          <b className={`is-${sevenStep.segment}`}>SOP-{sevenStep.segment}</b>
          <span>
            七步背调 {sevenStep.completed}/{sevenStep.total} 已完成 · 红旗 {sevenStep.redFlags} · 待核实 {sevenStep.reviewItems}
            {sevenStep.gaps.length ? <small>待补：{sevenStep.gaps.join("、")}</small> : null}
          </span>
          <button type="button" onClick={onOpenRating}>七步明细</button>
        </div>
      ) : null}
      {signals.length ? (
        <div className="research-verdict__signals" aria-label="风险与待核实项">
          {signals.slice(0, MAX_SIGNALS).map((item) => <Badge key={item.key} tone={item.tone}><span title={item.key}>{item.label}</span></Badge>)}
          {signals.length > MAX_SIGNALS ? <button type="button" onClick={onOpenRating}>另有 {signals.length - MAX_SIGNALS} 项</button> : null}
        </div>
      ) : null}
      {nextSteps.length ? (
        <div className="research-verdict__next">
          <strong><ListChecks size={15} />下一步建议</strong>
          <ol>{nextSteps.map((item) => <li key={item}>{item}</li>)}</ol>
        </div>
      ) : null}
    </section>
  );
}
